import { ReactComponent as IconChevronDown } from '@/assets/svgs/chevron-down.svg'
import { ReactComponent as IconExclamtionCircle } from '@/assets/svgs/icon-exclamation-circle.svg'
import { ReactComponent as IconExclamtion } from '@/assets/svgs/icon-exclamation.svg'
import { Fab } from '@mui/material'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import { useEffect, useMemo, useRef, useState } from 'react'
import Button from '../Button/index'
import Portal from '../Portal/index'
import Tooltip from '../Tooltip/Tooltip'
import { isEqual, uniqueId } from 'lodash'


/**
 * FloatingErrorButton
 * @param {object} props
 * @param {React.ReactNode} props.headerIcon
 * @param {string} props.headerTitle
 * @param {Array<{type: 'WARN' | 'ERROR' | undefined, message: string, ref: React.RefObject<Element>}>} props.errorList
 * @param {boolean} props.showOnlyWithError
 * @param {boolean} props.closable
 * @returns {JSX.Element}
 *
 * @example
 * <FloatingErrorButton errorList={[{message: "Err", ref: {}}]} />
 */

const ErrorList = ({ errorListToDisplay, setErrorListToDisplay, addClass }) => {
	if (errorListToDisplay?.length === 0) {
		return <div className="feb_row">No Error</div>
	}

	function scrollElementIntoView(elementId, index) {
		if (!elementId) {
			return;
		}

		const element = document.getElementById(elementId);
		if (element) {
			setErrorListToDisplay(errorListToDisplay.map((e, i) => {
				e.selected = i === index
				return e
			}))

			element.scrollIntoView({ behavior: 'smooth' });
			element?.parentElement?.parentElement?.getElementsByTagName('input')?.[0]?.focus();
		}
	}

	const display = errorListToDisplay?.map((s, index) => {
		const errorRowClass = classNames(
			addClass,
			{
				feb_row: true,
				feb_row_clickable: s.ref,
				selected: s.selected,
			}
		)

		return <div
			key={uniqueId()}
			tabIndex={s.ref ? 0 : -1}
			className={errorRowClass}
			onClick={() => { scrollElementIntoView(s.ref, index) }}
			onKeyDown={(e) => { if (e.key === 'Enter') { scrollElementIntoView(s.ref, index) } }}
		>
			{s?.message}
		</div>
	})

	return display;
}

ErrorList.propTypes = {
	errorListToDisplay: PropTypes.arrayOf(PropTypes.shape({
		type: PropTypes.oneOf(['WARN', 'ERROR', undefined]),
		message: PropTypes.string,
		ref: PropTypes.string,
	})),
	setErrorListToDisplay: PropTypes.func,
	addClass: PropTypes.string,
}

const FloatingErrorButton = ({
	headerIcon,
	headerTitle,
	errorList = [],
	showOnlyWithError = true,
	closable = false,
	addClass = '',
	useCSSEffects = true,
}) => {

	const ref = useRef(null)
	const [errorListToDisplay, setErrorListToDisplay] = useState([])
	const [visible, setVisible] = useState(false)
	const [initial, setInitial] = useState(true)

	const memoizedErrorList = useMemo(() => errorList, [errorList])

	useEffect(() => {
		if (!isEqual(errorListToDisplay, memoizedErrorList)) {
			setErrorListToDisplay(memoizedErrorList)
		}
	}, [memoizedErrorList])

	useEffect(() => {
		if (showOnlyWithError && errorList?.length === 0) {
			setVisible(false)
			return;
		}

		const checkIfClickedOutside = (e) => {
			if (visible && ref.current && !ref.current.contains(e.target) && closable === true) {
				setVisible(false)
			}
		}

		document.addEventListener("click", checkIfClickedOutside)

		if (useCSSEffects) {
			if (initial && visible) {
				document.getElementById('feb_button')?.classList?.replace('initial', 'feb_hidden');
				document.getElementById('feb_active')?.classList?.replace('initial', 'feb_visible');
				setInitial(false);
			} else if (visible) {
				document.getElementById('feb_button')?.classList?.replace('feb_visible', 'feb_hidden');
				document.getElementById('feb_active')?.classList?.replace('feb_hidden', 'feb_visible');
			} else {
				document.getElementById('feb_button')?.classList?.replace('feb_hidden', 'feb_visible');
				document.getElementById('feb_active')?.classList?.replace('feb_visible', 'feb_hidden');
			}
		}

		return () => {
			document.removeEventListener("click", checkIfClickedOutside)
		}
	}, [visible])

	const handleFabClick = (e) => {
		e.stopPropagation();
		setVisible(true);
	}

	const febClass = classNames(
		addClass,
		{
			feb: true,
			// feb_wrapper: true,
			initial: initial,
			open: visible,
			close: !visible,
			// open: !useCSSEffects && visible,
			// close: !useCSSEffects && !visible,
			// "open_effects": useCSSEffects && visible,
			// "close_effects": useCSSEffects && !visible,
		}
	)

	return (<>
		{((showOnlyWithError && errorList?.length) || !showOnlyWithError) && <>
			<div id='floating_error_button' className={febClass} ref={ref}>
				<div className='feb_wrapper'>
					{(useCSSEffects || (!useCSSEffects && !visible)) &&
						<Fab
							id='feb_button'
							className="feb_button initial"
							color="error"
							onClick={handleFabClick}
							data-testid='feb_button'
							data-tooltip-id="tooltip"
							data-tooltip-content={`Expand (${errorList?.length} errors)`}
						>
							<IconExclamtion />
						</Fab>
					}
					{(useCSSEffects || (!useCSSEffects && visible)) &&
						<div
							id="feb_active"
							data-testid="feb_active"
							className='feb_active initial'
						>
							<div className='feb_header'>
								<div className="feb_header_icon">
									{headerIcon ?? <IconExclamtionCircle />}
								</div>
								<div className="feb_header_title">
									{headerTitle ?? `${errorList?.length ? errorList?.length + ' fields require attention.' : ''}`}
								</div>
								<Button
									addClass={'feb_header_close'}
									ascent
									noborder
									round
									text={<IconChevronDown />}
									data-testid='feb_header_close'
									data-tooltip-id="tooltip"
									data-tooltip-content="Collapse"
									submit={() => {
										setVisible(false)
									}}
								/>
							</div>
							<div className='feb_body'>
								<ErrorList
									errorListToDisplay={errorListToDisplay}
									setErrorListToDisplay={setErrorListToDisplay}
									addClass={''}
								/>
							</div>
						</div>
					}
				</div>
			</div>

			<Portal id='tooltip'>
				<Tooltip id='tooltip' />
			</Portal>
		</>}
	</>)
}

FloatingErrorButton.propTypes = {
	headerIcon: PropTypes.elementType,
	headerTitle: PropTypes.string,
	errorList: PropTypes.arrayOf(PropTypes.shape({
		type: PropTypes.oneOf(['WARN', 'ERROR', undefined]),
		message: PropTypes.string,
		ref: PropTypes.string,
	})),
	showOnlyWithError: PropTypes.bool,
	closable: PropTypes.bool,
	addClass: PropTypes.string,
	useCSSEffects: PropTypes.bool,
};

export default FloatingErrorButton
